import { supabase } from '@/integrations/supabase/client';
import { SubscriptionTier } from '@/types'; // Ensure SubscriptionTier is imported if needed

export const fetchUserProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, display_name, created_at, is_verified_client')
    .eq('id', userId)
    .single();
    
  if (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
  return data;
};

const _ensureBasicProfileExists = async (id: string, displayName: string): Promise<boolean> => {
  try {
    const { data: existingProfile, error: checkError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', id)
      .maybeSingle(); // Use maybeSingle to handle null case without error

    // Throw actual errors encountered during the check
    if (checkError && checkError.code !== 'PGRST116') { // PGRST116: 'Object not found' is expected
        console.error('Error checking profile existence:', checkError);
        throw checkError; 
    }
    
    if (!existingProfile) {
       console.log(`Profile ${id} not found, creating...`);
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: id,
          display_name: displayName,
          role: 'client' // Assumes client role
        });
        
      if(insertError) {
           console.error('Error inserting profile:', insertError);
           throw insertError;
      }
      
      console.log('Created basic profile for user:', id);
    }
    return true; // Profile exists or was created
  } catch (error) {
    console.error('Error in _ensureBasicProfileExists:', error);
    return false;
  }
};

// Define a return type for clarity
type ProfileExistenceStatus = {
  existsInProfiles: boolean;
  existsInEscortProfiles: boolean;
};

export const ensureProfileExists = async (profileId: string, displayName?: string): Promise<ProfileExistenceStatus> => {
  let existsInProfiles = false;
  let existsInEscortProfiles = false;

  try {
    // 1. Check if profile already exists in 'profiles' table
    const { data: profileData, error: profileCheckError } = await supabase
      .from('profiles')
      .select('id') // Select only necessary field
      .eq('id', profileId)
      .maybeSingle();

    if (profileCheckError && profileCheckError.code !== 'PGRST116') { // Ignore 'not found' error
        console.error(`Error checking profile existence for ${profileId}:`, profileCheckError);
        throw profileCheckError;
    }

    if (profileData) {
      // console.log(`Profile ${profileId} already exists in profiles.`);
      existsInProfiles = true;
      // We still need to check escort_profiles even if it exists in profiles,
      // because the role might be important elsewhere.
    }

    // 2. Check if it's an escort, regardless of existence in 'profiles'
    const { data: escortData, error: escortCheckError } = await supabase
      .from('escort_profiles')
      .select('id, display_name') // Select only necessary fields
      .eq('id', profileId)
      .maybeSingle();

     if (escortCheckError && escortCheckError.code !== 'PGRST116') { // Ignore 'not found' error
        console.warn(`Error checking escort profile for ${profileId}:`, escortCheckError);
        // Do not throw here, as we might still proceed based on profileData
    }

    if (escortData) {
      existsInEscortProfiles = true;
      // console.log(`Profile ${profileId} exists in escort_profiles.`);
    }

    // 3. If profile does NOT exist in 'profiles', attempt to create ONLY IF it's NOT an escort
    if (!existsInProfiles && !existsInEscortProfiles) {
      console.log(`Profile ${profileId} does not exist in profiles or escort_profiles, attempting to create as client.`);
      let name = displayName || 'User'; // Use provided display name or default
      const role: 'client' | 'escort' = 'client'; // Implicitly client if not in escort_profiles

      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: profileId,
          display_name: name,
          role: role
        });

      if (insertError) {
        console.error(`Error inserting client profile for ${profileId}:`, insertError);
        // Propagate the error to be handled by the calling function
        throw insertError;
      }
      console.log(`Successfully created client profile for ${profileId}.`);
      existsInProfiles = true; // It now exists
    } else if (!existsInProfiles && existsInEscortProfiles) {
      // If it's an escort but not in 'profiles', log and do nothing, return status
      console.warn(`Escort profile ${profileId} exists in escort_profiles but not in profiles. Not creating implicitly.`);
    } else if (existsInProfiles && existsInEscortProfiles) {
        // console.log(`Profile ${profileId} exists in both profiles and escort_profiles.`);
    } else if (existsInProfiles && !existsInEscortProfiles) {
         // console.log(`Profile ${profileId} exists in profiles but not escort_profiles.`);
    }


    return { existsInProfiles, existsInEscortProfiles };

  } catch (error) {
    console.error(`Failed to ensure profile exists for ${profileId}:`, error);
    // Return a default error state, or rethrow, depending on desired error handling
    return { existsInProfiles: false, existsInEscortProfiles: false };
  }
};

export const fetchChatPartnerProfile = async (partnerId: string) => {
  try {
    // Try to find escort profile first
    const { data: escortProfile, error: escortError } = await supabase
      .from('escort_profiles')
      .select('*, escort_photos!escort_photos_escort_id_fkey(photo_url, is_main)')
      .eq('id', partnerId)
      .single();
      
    if (escortProfile) {
      let mainPhotoUrl = null;
      if (Array.isArray(escortProfile.escort_photos)) {
        const mainPhoto = escortProfile.escort_photos.find((photo: any) => photo.is_main);
        mainPhotoUrl = mainPhoto?.photo_url;
      } else if (escortProfile.escort_photos && escortProfile.escort_photos.is_main) {
        mainPhotoUrl = escortProfile.escort_photos.photo_url;
      }
      
      return {
        id: escortProfile.id,
        name: escortProfile.display_name || 'Escort',
        avatar: mainPhotoUrl,
        type: 'escort',
        isOnline: escortProfile.is_online,
        isVerified: escortProfile.is_verified
      };
    }
    
    // Try to find regular user profile if not an escort
    const { data: userProfile, error: userError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', partnerId)
      .single();
      
    if (userProfile) {
      return {
        id: userProfile.id,
        name: userProfile.display_name || 'User',
        avatar: null,
        type: userProfile.role === 'escort' ? 'escort' : 'client'
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching chat partner profile:', error);
    return null;
  }
};

export const fetchEscortProfileById = async (profileId: string) => {
  try {
    // Use the proper foreign key constraint for the relationship
    console.log('Fetching escort profile with ID:', profileId);
    
    const { data, error } = await supabase
      .from('escort_profiles')
      .select(`
        *,
        secrets,
        escort_photos!escort_photos_escort_id_fkey(id, photo_url, is_main, is_private)
      `)
      .eq('id', profileId)
      .single();
      
    if (error) {
      console.error('Error in fetchEscortProfileById:', error);
      throw error;
    }
    
    // Log successful fetch to aid debugging
    console.log('Successfully fetched escort profile:', profileId);
    return data;
  } catch (error) {
    console.error('Error fetching escort profile by ID:', error);
    throw error;
  }
};

export const fetchEscortServices = async (profileId: string) => {
  try {
    const { data, error } = await supabase
      .from('escort_services')
      .select('*')
      .eq('escort_id', profileId);
      
    if (error) {
      console.error('Error in fetchEscortServices:', error);
      throw error;
    }
    
    console.log(`Successfully fetched ${data?.length || 0} services for escort:`, profileId);
    return data || [];
  } catch (error) {
    console.error('Error fetching escort services:', error);
    throw error;
  }
};

export const fetchEscortRates = async (profileId: string) => {
  try {
    console.log('Fetching rates for escort profile ID:', profileId);
    
    const { data, error } = await supabase
      .from('escort_rates')
      .select('*')
      .eq('escort_id', profileId);
      
    if (error) {
      console.error('Error in fetchEscortRates:', error);
      throw error;
    }
    
    console.log(`Successfully fetched ${data?.length || 0} rates for escort:`, profileId);
    console.log('Rates data:', data);
    return data || [];
  } catch (error) {
    console.error('Error fetching escort rates:', error);
    throw error;
  }
};

export const fetchEscortRatesByVisibility = async (profileId: string, visibility: string[] = ['public']) => {
  try {
    console.log(`Fetching ${visibility.join(', ')} rates for escort:`, profileId);
    
    const { data, error } = await supabase
      .from('escort_rates')
      .select('*')
      .eq('escort_id', profileId)
      .in('visibility', visibility);
      
    if (error) {
      console.error('Error in fetchEscortRatesByVisibility:', error);
      throw error;
    }
    
    console.log(`Successfully fetched ${data?.length || 0} rates with visibility ${visibility.join(', ')} for escort:`, profileId);
    return data || [];
  } catch (error) {
    console.error('Error fetching escort rates by visibility:', error);
    throw error;
  }
};

// Moved from auth-methods
export const upgradeSubscription = async (userId: string, tier: SubscriptionTier): Promise<{ success: boolean, error: any }> => {
  console.log(`Service: Upgrading subscription for user ${userId} to ${tier}`);
  try {
    // Calculer la date d'expiration (ex: 1 mois à partir de maintenant)
    const expiresAt = new Date();
    expiresAt.setMonth(expiresAt.getMonth() + 1);

    const { error } = await supabase
      .from('subscriptions') // Utilise subscriptions
      .upsert({
        id: userId, // <- Corrigé de user_id à id
        tier: tier,
        expires_at: expiresAt.toISOString(),
        // updated_at: new Date().toISOString() // Si la table subscriptions a une colonne updated_at
      }, { onConflict: 'id' }); // <- Corrigé onConflict pour utiliser 'id'

    // TODO: Consider logic for existing subscriptions (e.g., deactivate old, update end date?)
    // This currently just adds a new active record.

    console.log(`Service: Subscription upgraded and record created for user ${userId}`);
    return { success: true, error: null };
  } catch (error) {
    console.error('Service Error: in upgradeSubscription:', error);
    return { success: false, error }; // Return error object for handling
  }
};

// Nouvelle fonction pour mettre à jour les paramètres IA de l'escort
export const updateEscortAiSettings = async (
  escortId: string,
  settings: { // Utiliser Partial pour permettre les mises à jour partielles si nécessaire
    ai_chat_enabled?: boolean;
    ai_chat_response_mode?: 'always' | 'scheduled';
    ai_chat_schedule?: any; // Type JSONB
  }
): Promise<void> => {
  console.log(`Updating AI settings for escort ${escortId}:`, settings);

  // On cible la table 'profiles' car c'est là qu'on a ajouté les colonnes.
  // !! Vérifiez que c'est bien la table où sont ces colonnes !
  // Assurez-vous que les RLS permettent à l'utilisateur connecté (l'escort elle-même)
  // de mettre à jour ces colonnes pour son propre profil.
  const { error } = await supabase
    .from('profiles')
    .update({
      ai_chat_enabled: settings.ai_chat_enabled,
      ai_chat_response_mode: settings.ai_chat_response_mode,
      ai_chat_schedule: settings.ai_chat_schedule,
      // Mettre à jour une colonne updated_at si elle existe
      updated_at: new Date().toISOString(),
    })
    .eq('id', escortId); // !! Adapter la colonne ID si nécessaire

  if (error) {
    console.error(`Error updating AI settings for escort ${escortId}:`, error);
    throw error; // Rethrow pour que le composant UI puisse le gérer (ex: afficher un toast)
  }

  console.log(`Successfully updated AI settings for escort ${escortId}.`);
};

export const getSubscriptionStatus = async (userId: string): Promise<{ tier: SubscriptionTier | null, expiresAt: string | null }> => {
  const { data, error } = await supabase
    .from('subscriptions') 
    .select('tier, expires_at')
    .eq('id', userId) // <- Corrigé de user_id à id
    .order('expires_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching subscription status:', error);
    return { tier: null, expiresAt: null };
  }

  if (data) {
    return {
      tier: data.tier as SubscriptionTier | null,
      expiresAt: data.expires_at
    };
  }

  return { tier: null, expiresAt: null };
};
